import * as React from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { actionTaken } from '../../ReduxApp/app/user-sessions/duck/operations';
import { AvailableActions } from '../../ReduxApp/app/user-sessions/duck/actionTypes';

import { InputWrapper, IconWrapper, StyledInputSmall, StyledInputMedium, StyledInputLarge, BookingInputSmall, BookingInputMedium, BookingInputLarge, DisabledInputSmall, DisabledInputMedium, DisabledInputLarge } from './InputFieldStyles';

export type Props = {
	icon?: React.ReactNode;
	label?: string;
	placeholder?: string;
	value?: string;
	defaultValue?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onEnter?: () => void;
	fullWidth?: boolean;
	userAction?: string;
	tabIndex?: number;
	disabled?: boolean;
	style?: React.CSSProperties;
	size?: 'small' | 'medium' | 'large';
	type?: React.HTMLInputTypeAttribute;
	name?: string;
	error?: boolean;
	helperText?: React.ReactNode;
	required?: boolean;
	autoComplete?: string;
	bookingInput?: boolean;
	className?: string;
};

export const InputFieldLayout: React.FC<Props> = (props) => {
	const { icon, label, placeholder, value, defaultValue, onChange, onBlur, onFocus, onEnter, fullWidth, userAction, tabIndex, disabled, style, size = 'medium', type = 'text', name, error, helperText, required, autoComplete, bookingInput = false, className } = props;

	const location = useLocation();
	const dispatch = useDispatch();

	// Prefer a more specific analytics action if it exists in your enum.
	// Falls back to BUTTON_PRESSED to avoid compile-time errors if not present.
	const ANALYTIC = ((AvailableActions as any).INPUT_EDITED ?? (AvailableActions as any).INPUT_CHANGED ?? AvailableActions.BUTTON_PRESSED) as AvailableActions;

	const fireAnalytics = (why: string) => {
		try {
			if (userAction) {
				dispatch(actionTaken(ANALYTIC, `${userAction}:${why}`, location.pathname));
			}
		} catch {
			// no-op: keep layout resilient even if analytics wiring changes
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) onChange(e);
		fireAnalytics('change');
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		if (onFocus) onFocus(e);
		fireAnalytics('focus');
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		if (onBlur) onBlur(e);
		fireAnalytics('blur');
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && onEnter) {
			fireAnalytics('enter');
			onEnter();
		}
	};

	let InputComp: React.ElementType;
	switch (size) {
		case 'small':
			InputComp = StyledInputSmall;
			if (bookingInput) InputComp = BookingInputSmall;
			if (disabled) InputComp = DisabledInputSmall;
			break;
		case 'medium':
			InputComp = StyledInputMedium;
			if (bookingInput) InputComp = BookingInputMedium;
			if (disabled) InputComp = DisabledInputMedium;
			break;
		case 'large':
			InputComp = StyledInputLarge;
			if (bookingInput) InputComp = BookingInputLarge;
			if (disabled) InputComp = DisabledInputLarge;
			break;
		default:
			InputComp = StyledInputMedium;
	}

	// Build a stable className akin to your CTA (label/name -> kebab/underscore)
	const generatedClassName = className ?? (label || name || 'input').toLowerCase().replace(/\s+/g, '_');

	const adornment = !icon ? undefined : (
		<InputAdornment position="start">
			<IconWrapper>{icon}</IconWrapper>
		</InputAdornment>
	);

	// Collect all props we pass to the underlying Input component into a single object
	const outgoingProps: any = {
		variant: 'outlined',
		type,
		name,
		label,
		placeholder,
		value,
		defaultValue,
		onChange: handleChange,
		onFocus: handleFocus,
		onBlur: handleBlur,
		onKeyDown: handleKeyDown,
		fullWidth: !!fullWidth,
		tabIndex,
		disabled: !!disabled,
		error: !!error,
		helperText,
		required: !!required,
		autoComplete,
		size: size === 'large' ? 'medium' : size, // MUI supports 'small' | 'medium'; we handle 'large' via styles
		className: generatedClassName,
		sx: style,
		InputProps: {
			startAdornment: adornment,
		},
	};

	const returnValue = (
		<InputWrapper>
			<InputComp {...outgoingProps} />
		</InputWrapper>
	);
	return returnValue;
};
